/**
 * @fileoverview Modal de referência de atalhos de teclado.
 *
 * Aberto ao pressionar "?" (fora de inputs).
 * Agrupa atalhos por categoria: Geral, Navegação, Tasks, Editor.
 * Detecta automaticamente Mac (Cmd) vs Windows (Ctrl).
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Keyboard, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Wrap the `query` substring(s) in `text` with a highlight span so the
 * user sees what matched. Case-insensitive, splits on the first match
 * occurrence (good enough for short labels). Returns ReactNode[] to keep
 * inline rendering trivial.
 */
function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-secondary/30 text-foreground rounded-sm px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
const mod = isMac ? '⌘' : 'Ctrl';

interface ShortcutGroup {
  title: string;
  shortcuts: { keys: string[]; label: string }[];
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: 'Geral',
    shortcuts: [
      { keys: [mod, 'K'], label: 'Busca rápida (Command Palette)' },
      { keys: ['/'], label: 'Foca a busca da página (ou abre Command Palette)' },
      { keys: ['?'], label: 'Atalhos de teclado' },
      { keys: [mod, 'Shift', 'F'], label: 'Busca avançada' },
      { keys: ['Esc'], label: 'Fechar modal / Cancelar' },
    ],
  },
  {
    title: 'Navegação',
    shortcuts: [
      { keys: [mod, '1'], label: 'Dashboard' },
      { keys: [mod, '2'], label: 'Pipeline' },
      { keys: [mod, '3'], label: 'Leads' },
      { keys: [mod, '4'], label: 'Clientes' },
      { keys: [mod, '5'], label: 'Configurações' },
      { keys: ['G', 'D'], label: 'Ir para Dashboard (chord)' },
      { keys: ['G', 'P'], label: 'Ir para Pipeline (chord)' },
      { keys: ['G', 'L'], label: 'Ir para Leads (chord)' },
      { keys: ['G', 'C'], label: 'Ir para Clientes (chord)' },
      { keys: ['G', 'W'], label: 'Ir para Workspaces (chord)' },
    ],
  },
  {
    title: 'CRM',
    shortcuts: [
      { keys: ['N'], label: 'Novo lead (no Pipeline ou Leads)' },
      { keys: ['F'], label: 'Foco do dia (triagem dos leads)' },
      { keys: ['J'], label: 'Próximo lead no Pipeline' },
      { keys: ['K'], label: 'Lead anterior no Pipeline' },
      { keys: ['Shift', 'J/K'], label: 'Estender seleção entre cards' },
      { keys: ['Enter'], label: 'Abrir lead em foco' },
      { keys: ['C'], label: 'Abrir lead em foco direto na aba Logs' },
      { keys: ['S'], label: 'Star/unstar lead em foco (watchlist)' },
      { keys: ['Shift', '1-9'], label: 'Mover lead em foco para coluna N' },
      { keys: ['Esc'], label: 'Limpar foco do lead' },
      { keys: ['Shift', 'Click'], label: 'Selecionar intervalo (Leads)' },
      { keys: [mod, 'Enter'], label: 'Salvar lead (no AddLeadDialog)' },
      { keys: [mod, 'Shift', 'Enter'], label: 'Salvar e adicionar outro (chain)' },
    ],
  },
  {
    title: 'Editor',
    shortcuts: [
      { keys: [mod, 'B'], label: 'Negrito' },
      { keys: [mod, 'I'], label: 'Itálico' },
      { keys: [mod, 'S'], label: 'Salvar' },
      { keys: [mod, 'Z'], label: 'Desfazer' },
      { keys: [mod, 'Shift', 'Z'], label: 'Refazer' },
    ],
  },
];

/**
 * Slug -> readable group title for the path-based contextual scroll.
 * Picks the most relevant section to land on when the user opens the
 * cheat sheet from a given page.
 */
function pickInitialGroup(pathname: string): string {
  if (pathname.startsWith('/pipeline') || pathname.startsWith('/leads') || pathname.startsWith('/clientes')) return 'CRM';
  return 'Geral';
}

/** stable id for the section <h4> so we can scrollIntoView. */
const groupId = (title: string) => `kbd-group-${title.toLowerCase().replace(/\s+/g, '-')}`;

const EXPANDED_KEY = 'bl-kbd-cheatsheet-expanded';

export default function KeyboardShortcutsModal() {
  const [open, setOpen] = useState(false);
  // Each group can be expanded/collapsed individually. State persists
  // in localStorage so the modal reopens to the same shape. Default
  // includes "Geral" — universal shortcuts always relevant.
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(EXPANDED_KEY);
      return new Set<string>(raw ? JSON.parse(raw) : ['Geral']);
    } catch { return new Set<string>(['Geral']); }
  });
  // Inline filter — when non-empty, hides shortcuts that don't match
  // (label or key). Groups with at least one match auto-expand so the
  // user can see the result.
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SHORTCUT_GROUPS.map(g => ({ ...g, matched: false }));
    return SHORTCUT_GROUPS.map(group => {
      const shortcuts = group.shortcuts.filter(s => {
        if (s.label.toLowerCase().includes(q)) return true;
        if (s.keys.some(k => k.toLowerCase().includes(q))) return true;
        return false;
      });
      return { ...group, shortcuts, matched: shortcuts.length > 0 };
    });
  }, [query]);
  const persistExpanded = (next: Set<string>) => {
    try { localStorage.setItem(EXPANDED_KEY, JSON.stringify([...next])); } catch { /* empty */ }
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== '?' || e.metaKey || e.ctrlKey) return;
      const target = e.target as HTMLElement;
      const isTyping = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
        target.contentEditable === 'true';
      // While the dialog is open we honor "?" even from inside the
      // search input — symmetric toggle so the user can close from
      // anywhere. When closed and typing elsewhere, bail to avoid
      // hijacking real "?" inputs.
      if (isTyping && !open) return;
      e.preventDefault();
      setOpen(o => !o);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // On open, ensure the contextual section is expanded + scroll to it.
  // Reset the search query so reopening starts clean. requestAnimationFrame
  // ensures the DOM has the section mounted before we try to query for it.
  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    const target = pickInitialGroup(window.location.pathname);
    setExpanded(prev => {
      const next = new Set(prev);
      next.add(target);
      next.add('Geral'); // Geral always expanded — universal shortcuts
      persistExpanded(next);
      return next;
    });
    requestAnimationFrame(() => {
      const el = document.getElementById(groupId(target));
      el?.scrollIntoView({ block: 'start', behavior: 'instant' as ScrollBehavior });
    });
  }, [open]);

  const toggleGroup = (title: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      persistExpanded(next);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="h-5 w-5 text-secondary" />
            Atalhos de Teclado
          </DialogTitle>
          <DialogDescription>
            Pressione <kbd className="px-1.5 py-0.5 text-[10px] bg-muted border border-border rounded font-mono">?</kbd> para abrir/fechar
          </DialogDescription>
        </DialogHeader>

        <div className="relative mt-2">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              // First Esc clears a non-empty query; second Esc lets
              // Radix close the dialog. Common search-input convention.
              if (e.key === 'Escape' && query) {
                e.preventDefault();
                e.stopPropagation();
                setQuery('');
              }
            }}
            placeholder="Filtrar atalhos por nome ou tecla..."
            className="h-9 pl-8 pr-16 text-sm"
            autoFocus
          />
          {query ? (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={() => setQuery('')}
              aria-label="Limpar busca"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 h-5 px-1 rounded bg-muted text-[10px] font-mono text-muted-foreground pointer-events-none">
              Esc
            </kbd>
          )}
        </div>

        {query.trim() && !filtered.some(g => g.shortcuts.length > 0) && (
          <div className="mt-6 text-center text-sm text-muted-foreground">
            Nenhum atalho corresponde a <span className="font-mono text-foreground">"{query}"</span>
          </div>
        )}

        <div className="space-y-2 mt-3">
          {filtered.map(group => {
            // While searching, force-expand groups with matches; collapse
            // empty ones. Outside search, use the user's persisted state.
            const isSearching = !!query.trim();
            const isOpen = isSearching ? group.shortcuts.length > 0 : expanded.has(group.title);
            if (isSearching && group.shortcuts.length === 0) return null;
            return (
              <div key={group.title} className="border border-border/40 rounded-md">
                <button
                  type="button"
                  id={groupId(group.title)}
                  onClick={() => toggleGroup(group.title)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider scroll-mt-2 hover:text-foreground transition-colors"
                  aria-expanded={isOpen}
                >
                  <span>{group.title}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-[10px] font-normal text-muted-foreground/60 normal-case tracking-normal">
                      {group.shortcuts.length} atalho{group.shortcuts.length > 1 ? 's' : ''}
                    </span>
                    <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', isOpen && 'rotate-180')} />
                  </span>
                </button>
                {isOpen && (
                  <div className="space-y-0.5 px-2 pb-2">
                    {group.shortcuts.map((shortcut) => (
                      <div key={shortcut.label} className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/50 transition-colors">
                        <span className="text-sm text-foreground">{highlightMatch(shortcut.label, query.trim())}</span>
                        <div className="flex items-center gap-1">
                          {shortcut.keys.map((key, ki) => {
                            const q = query.trim().toLowerCase();
                            const matched = q && key.toLowerCase().includes(q);
                            return (
                              <kbd
                                key={ki}
                                className={cn(
                                  'inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 text-[11px] font-mono font-medium bg-muted border border-border rounded shadow-sm text-muted-foreground',
                                  matched && 'bg-secondary/30 text-foreground border-secondary/50',
                                )}
                              >
                                {key}
                              </kbd>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4 pt-3 border-t border-border">
          <p className="text-[11px] text-muted-foreground text-center">
            Dica: Use <kbd className="px-1 py-0.5 text-[10px] bg-muted border border-border rounded font-mono">{mod}+K</kbd> para busca rápida
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
